import { LeftOutlined } from '@ant-design/icons';
import { Species, SpeciesSurveyDataV1 } from '@omniafishing/core';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useState } from 'react';
import { useResponsive } from '../../../../../hooks/use_responsive';
import { determineLunkerYear } from '../../../../../lib/species_survey_data';
import { LoadableChartCpue } from './loadable_chart_cpue';
import { LoadableChartLengths } from './loadable_chart_lengths';
import { LunkerPotential } from './lunker_potential';
import { abundanceTextColorClassMap, abundanceTextMap } from './species_card';
import { SvgAbundance } from './svg_abundance';

interface SpeciesDetailProps {
  onBackClick: () => void;
  species: Species;
  speciesData: SpeciesSurveyDataV1['species'][keyof SpeciesSurveyDataV1['species']];
}

export const LunkerDescription = (
  <>
    <p className="text-[11px]">
      In the most recent DNR sampling event (where biologists catch and measure fish to monitor lake
      health), at least one fish met or exceeded a trophy size threshold in the last 5 years:
    </p>
    <ul className="list-disc text-[11px] pl-3">
      <li>Bass (Largemouth or Smallmouth): 20”</li>
      <li>Walleye: 28”</li>
      <li>Northern Pike: 35”</li>
      <li>Muskie: 40”</li>
    </ul>
  </>
);

export const SpeciesDetail = (props: SpeciesDetailProps) => {
  const { onBackClick, species, speciesData } = props;

  const { isMobile } = useResponsive();

  const { current_status } = speciesData;
  const { abundance_quartile, primary_gear_type } = current_status;
  const allYears = Object.keys(speciesData.length_distributions);
  const [selectedYear, setSelectedYear] = useState(allYears[allYears.length - 1]);
  const lunkerYear = determineLunkerYear(species.display_name, speciesData);

  const [emblaRef] = useEmblaCarousel(
    {
      align: 'start',
      startIndex: allYears.length - 1,
      dragFree: true,
    },
    []
  );

  return (
    <div className="p-3 overflow-hidden">
      <div
        onClick={onBackClick}
        className="flex items-center gap-2 cursor-pointer border-b border-black pb-3 mb-3"
      >
        <div className="border border-black rounded-full flex items-center justify-center w-7 h-7">
          <LeftOutlined />
        </div>
        <span className="font-termina font-bold">All Species</span>
      </div>
      <h2 className="text-xl font-bold mb-3">{species.display_name}</h2>
      {lunkerYear && (
        <div className="flex">
          <LunkerPotential year={lunkerYear} />
        </div>
      )}
      <img src={species.image} alt={`picture of ${species.display_name}`} />

      <div ref={emblaRef} className="overflow-hidden -mx-6 pr-6 select-none">
        <div className="flex gap-3 pl-6">
          {allYears.map((year) => {
            return (
              <div
                key={year}
                className={classNames(
                  'text-2xl font-bold py-1 px-4 border-2 rounded-xl cursor-pointer',
                  {
                    'border-black': selectedYear === year,
                    'border-black/15': selectedYear !== year,
                  }
                )}
                onClick={() => setSelectedYear(year)}
              >
                {year}
              </div>
            );
          })}
        </div>
      </div>

      <div className="py-6 border-b border-black">
        <h3 className="font-bold text-sm text-[#061622] mb-3">{selectedYear} Lengths</h3>
        <div className="p-3 bg-white rounded-lg">
          <LoadableChartLengths
            width={isMobile ? 340 : 470}
            height={200}
            speciesData={speciesData}
            selectedYear={selectedYear}
          />
        </div>
      </div>

      <div className="py-6">
        <h3 className="font-bold text-sm text-[#061622] mb-3">Abundance (CPUE)</h3>
        <div>
          <div className="p-3 bg-white rounded-lg mb-2">
            {abundance_quartile && (
              <div className="mb-3">
                <p
                  className={`text-xs font-bold ${abundanceTextColorClassMap[abundance_quartile]}`}
                >
                  {abundanceTextMap[abundance_quartile]}
                </p>
                <p className="text-[11px] text-[#6B7172] mb-2">Current Abundance</p>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4].map((quartile) => (
                    <SvgAbundance
                      key={quartile}
                      className={
                        quartile <= abundance_quartile
                          ? abundanceTextColorClassMap[abundance_quartile]
                          : 'text-[#D5D7D9]'
                      }
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-[10px] text-[#6B7172]">Historical CPUE</span>
              <span className="text-[11px] font-bold">{primary_gear_type}</span>
            </div>
            <LoadableChartCpue
              width={isMobile ? 340 : 470}
              height={200}
              speciesData={speciesData}
            />
          </div>
          <p className="text-[11px] text-[#6B7172] mb-4">
            Source: Minnesota Department of Natural Resources
          </p>

          <section className="flex flex-col gap-3">
            <div>
              <p className="font-termina text-[11px] font-bold">Understanding Abundance</p>
              <p className=" text-[11px]">
                Fish abundance shows how many fish are in a lake based on scientific surveys. This
                is measured using <strong>Catch Per Unit Effort (CPUE)</strong>, which tracks how
                many fish are caught in a set time using standardized methods. A higher CPUE means
                more fish, but factors like season, water temperature, and fish behavior still
                affect catch rates.{' '}
                <strong>
                  Even in lakes with fewer fish, knowing the right spots, lures, and fishing times
                  can lead to success.
                </strong>
              </p>
            </div>

            {lunkerYear && (
              <div>
                <p className="font-termina text-[11px] font-bold">What Is Lunker Potential?</p>
                <p className="text-[11px]">This lake has Lunker Potential.</p>
                {LunkerDescription}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};
